/**
 * What a business may do on its plan (BIL-1, strategy/01 §6.2). One source for the browser, which
 * greys out what is not included, and for PocketBase, which refuses it: this file is bundled into
 * server/pb_hooks/lib/plan.js (`pnpm --filter server build:hooks`), so keep it free of imports,
 * Intl and Unicode property escapes.
 *
 *  - Start €9: 30 receipts a month, 1 card or bank account, 1 user, no BMD/RZL/DATEV export.
 *  - Standard €19: receipts without limit, 3 accounts, 3 users, the accountant's formats.
 *  - Plus €29: no limits.
 *  - The accountant role never counts as a user.
 *  - A new business has 14 days of Standard without a card. After that, without a plan, it is
 *    read-only: everything stays visible and exportable, nothing new can be added or changed.
 *  - A downgrade keeps everything; only new use beyond the limit is refused.
 *  - A failed payment keeps the plan for 7 days from the first failure while Stripe retries; then
 *    the business is read-only until the payment goes through (BIL-2).
 */

export type PlanId = "start" | "standard" | "plus";
export type Interval = "month" | "year";
export const PLAN_IDS: PlanId[] = ["start", "standard", "plus"];
export const TRIAL_DAYS = 14;
export const GRACE_DAYS = 7;
const DAY_MS = 86400000;

export type Limits = {
  /** Receipts added per calendar month (any channel); null = no limit. */
  receiptsPerMonth: number | null;
  /** Cards and bank accounts. */
  accounts: number | null;
  /** People with access, the owner included and accountants not. */
  users: number | null;
  /** BMD, RZL and DATEV files; the period ZIP with CSV is in every plan. */
  accountingExport: boolean;
};

export const LIMITS: Record<PlanId, Limits> = {
  start: { receiptsPerMonth: 30, accounts: 1, users: 1, accountingExport: false },
  standard: { receiptsPerMonth: null, accounts: 3, users: 3, accountingExport: true },
  plus: { receiptsPerMonth: null, accounts: null, users: null, accountingExport: true },
};

/** Stripe statuses under which the plan applies; past_due only within the grace period. */
const PAYING = ["active", "trialing"];

export type PlanState =
  | { kind: "paid"; plan: PlanId; status: string }
  | { kind: "trial"; plan: "standard"; endsAt: string; daysLeft: number }
  | { kind: "expired"; trialEndedAt: string };

export type PlanInput = {
  trialEndsAt?: string | null;
  subscription?: { plan: string; status: string; pastDueSince?: string | null } | null;
};

const isPlan = (value: string): value is PlanId => PLAN_IDS.indexOf(value as PlanId) !== -1;
/** PocketBase writes dates as "2026-10-24 10:00:00.000Z". */
const time = (value: string) => new Date(value.replace(" ", "T")).getTime();

/** Whether a past_due subscription is still within its grace period. */
function inGrace(pastDueSince: string | null | undefined, now: Date): boolean {
  // A row from before the grace period was recorded: Stripe's own retries decide.
  if (!pastDueSince) return true;
  return now.getTime() - time(pastDueSince) < GRACE_DAYS * DAY_MS;
}

export function planState(input: PlanInput, now: Date = new Date()): PlanState {
  const subscription = input.subscription;
  if (
    subscription &&
    isPlan(subscription.plan) &&
    (PAYING.indexOf(subscription.status) !== -1 ||
      (subscription.status === "past_due" && inGrace(subscription.pastDueSince, now)))
  ) {
    return { kind: "paid", plan: subscription.plan, status: subscription.status };
  }
  const endsAt = input.trialEndsAt || "";
  const left = endsAt ? time(endsAt) - now.getTime() : 0;
  if (left > 0) {
    return { kind: "trial", plan: "standard", endsAt, daysLeft: Math.ceil(left / DAY_MS) };
  }
  return { kind: "expired", trialEndedAt: endsAt };
}

/** The limits that apply; null while the business is read-only. */
export function limitsFor(state: PlanState): Limits | null {
  return state.kind === "expired" ? null : LIMITS[state.plan];
}

export type Usage = { receiptsThisMonth: number; accounts: number; users: number };

export type PlanAction = "write" | "receipt" | "account" | "user" | "accountingExport";

/** Why an action is refused, or null when it is allowed. Counts are before the action. */
export type Refusal = "read_only" | "receipts" | "accounts" | "users" | "accounting_export";

export function refusal(
  state: PlanState,
  action: PlanAction,
  usage?: Partial<Usage>,
): Refusal | null {
  const limits = limitsFor(state);
  if (!limits) return "read_only";
  const over = (limit: number | null, count: number | undefined) =>
    limit !== null && (count || 0) >= limit;
  if (action === "receipt" && over(limits.receiptsPerMonth, usage && usage.receiptsThisMonth)) {
    return "receipts";
  }
  if (action === "account" && over(limits.accounts, usage && usage.accounts)) return "accounts";
  if (action === "user" && over(limits.users, usage && usage.users)) return "users";
  if (action === "accountingExport" && !limits.accountingExport) return "accounting_export";
  return null;
}

/** First instant of the calendar month `now` is in, UTC, as PocketBase stores dates. */
export function monthStart(now: Date = new Date()): string {
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  return start.toISOString().replace("T", " ");
}

/** The prefix PocketBase error messages carry when a plan refuses something. */
export const REFUSAL_PREFIX = "plan:";
